import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ContactMultiPicker from "./ContactMultiPicker";

const nowLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

// Gesprächsnotiz / Telefonat / Notiz dokumentieren
export default function NoteDialog({ open, onOpenChange, scope, inReplyTo, onSaved }) {
  const [f, setF] = useState({ kind: "call", direction: "internal", subject: "", content: "", occurred_at: nowLocal(), follow_up_date: "", contact_ids: [] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.subject.trim()) { toast.error("Bitte einen Betreff angeben"); return; }
    setBusy(true);
    try {
      await api.post("/communications/notes", {
        ...f, ...scope, occurred_at: new Date(f.occurred_at).toISOString(),
        follow_up_date: f.follow_up_date || null, in_reply_to_id: inReplyTo?.id || null,
        contact_ids: [...new Set([...(f.contact_ids || []), ...(scope.contact_id ? [scope.contact_id] : [])])],
      });
      toast.success("Notiz dokumentiert");
      onOpenChange(false);
      setF((s) => ({ ...s, subject: "", content: "", follow_up_date: "" }));
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="comm-note-dialog">
        <DialogHeader><DialogTitle>Gesprächsnotiz dokumentieren</DialogTitle></DialogHeader>
        {inReplyTo && <div className="text-xs text-slate-500">Bezug: {inReplyTo.subject}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Art</Label>
            <Select value={f.kind} onValueChange={(v) => set("kind", v)}>
              <SelectTrigger data-testid="comm-note-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="call">Telefonat</SelectItem><SelectItem value="meeting">Gespräch / Termin</SelectItem><SelectItem value="note">Notiz</SelectItem></SelectContent>
            </Select></div>
          <div><Label>Richtung</Label>
            <Select value={f.direction} onValueChange={(v) => set("direction", v)}>
              <SelectTrigger data-testid="comm-note-direction"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="in">Eingehend (Anruf erhalten)</SelectItem><SelectItem value="out">Ausgehend (Anruf getätigt)</SelectItem><SelectItem value="internal">Intern</SelectItem></SelectContent>
            </Select></div>
          <div className="col-span-2"><Label>Betreff</Label>
            <Input value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="comm-note-subject" /></div>
          <div className="col-span-2"><Label>Gesprächspartner</Label>
            <ContactMultiPicker value={f.contact_ids} onChange={(v) => set("contact_ids", v)} testId="comm-note-contacts" /></div>
          <div className="col-span-2"><Label>Inhalt</Label>
            <Textarea rows={5} value={f.content} onChange={(e) => set("content", e.target.value)} data-testid="comm-note-content" /></div>
          <div><Label>Datum & Uhrzeit</Label>
            <Input type="datetime-local" value={f.occurred_at} onChange={(e) => set("occurred_at", e.target.value)} data-testid="comm-note-date" /></div>
          <div><Label>Wiedervorlage (optional)</Label>
            <Input type="date" value={f.follow_up_date} onChange={(e) => set("follow_up_date", e.target.value)} data-testid="comm-note-followup" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="comm-note-save">{busy ? "Speichere…" : "Dokumentieren"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
